import React, { useState } from 'react';
import { Container, Row, Col, Form, Button, Card, Table, Alert } from 'react-bootstrap';
import { API_URL } from '../../../utils/constants';
import './GroupsFindScreen.css';

const GroupsFindScreen = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!searchTerm.trim()) {
      setError('Please enter a search term');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${API_URL}/groups/search/${encodeURIComponent(searchTerm)}`);
      if (!response.ok) {
        throw new Error('Failed to search groups');
      }
      
      const data = await response.json();
      setSearchResults(data);
    } catch (error) {
      console.error('Search error:', error);
      setError('Error searching groups. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleClear = () => {
    setSearchTerm('');
    setSearchResults([]);
    setError('');
  };

  return (
    <Container fluid className="groups-find-screen">
      <Row className="mb-4">
        <Col>
          <h2>Find Groups</h2>
          <p className="text-muted">Search for groups by number, name, city, or type</p>
        </Col>
      </Row>

      <Row className="mb-4">
        <Col md={6}>
          <Form onSubmit={handleSearch}>
            <div className="d-flex gap-2">
              <Form.Control
                type="text"
                placeholder="Search groups by number, name, city, or type..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <Button type="submit" variant="primary" size="sm" style={{ minWidth: 'fit-content' }}>
                Search
              </Button>
            </div>
          </Form>
        </Col>
        <Col md={6} className="text-end">
          <Button variant="outline-secondary" onClick={handleClear} disabled={!searchTerm && searchResults.length === 0}>
            Clear
          </Button>
        </Col>
      </Row>

      {error && (
        <Row className="mb-3">
          <Col>
            <Alert variant="danger" onClose={() => setError('')} dismissible>
              {error}
            </Alert>
          </Col>
        </Row>
      )}

      {loading && (
        <Row className="mb-3">
          <Col>
            <div className="text-center">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          </Col>
        </Row>
      )}

      {searchResults.length > 0 && (
        <Row>
          <Col>
            <Card>
              <Card.Header>
                <h5 className="mb-0">Search Results ({searchResults.length})</h5>
              </Card.Header>
              <Card.Body>
                <Table responsive striped hover>
                  <thead>
                    <tr>
                      <th>Group #</th>
                      <th>Name</th>
                      <th>Type</th>
                      <th>City</th>
                      <th>State</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {searchResults.map((group) => (
                      <tr key={group.id}>
                        <td>
                          <strong>{group.group_number}</strong>
                        </td>
                        <td>{group.name}</td>
                        <td>{group.group_type_name || 'N/A'}</td>
                        <td>{group.city || 'N/A'}</td>
                        <td>{group.state || 'N/A'}</td>
                        <td>
                          <span className={`badge ${group.active ? 'bg-success' : 'bg-danger'}`}>
                            {group.active ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}

      {searchResults.length === 0 && !loading && searchTerm && (
        <Row>
          <Col>
            <Alert variant="info">
              No groups found matching your search criteria.
            </Alert>
          </Col>
        </Row>
      )}
    </Container>
  );
};

export default GroupsFindScreen;
